import axios from "axios";
import dayjs from "dayjs";
import { StockEvents, StockEventsStatistics, StockEventsTranscripts, StockEventsUpcoming, StockEventsUpcomingHeaders, NewStockEventsStatistics } from "@/models/stock-events";
import { EventListRow, EventListHeaders } from "@/models/event-page";
import { List, ListQueryParams } from "@/models/table";

export const getStockEventsTranscripts = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, universe_id, next_page_url } = params;

    const url = next_page_url || `/API/v2/id/${universe_id}/transcripts/list?&dgformat`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get<StockEventsTranscripts>(url, {
        params: {
            ...defaultParams,
        },
    });

    return res.data;
};

export const getStockEventTranscriptDetailByEventId = async (event_id: number, externalId?: number) => {
    // ?external_id= (CIQ keydevid) disambiguates across event models whose PKs collide;
    // the backend falls back to the path PK when absent, so the legacy page is unaffected.
    const q = externalId != null ? `?external_id=${externalId}` : "";
    const res = await axios.get<StockEventsTranscripts>(`/interface/api/events/${event_id}/get_transcript${q}`);
    return res.data;
};

export const getStockEventsUpcoming = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url, data } = params;

    const url = next_page_url || `/interface/api/events/company/${data.company_id}/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get<List<StockEventsUpcoming, StockEventsUpcomingHeaders>>(url, {
        params: {
            ...defaultParams,
        },
        signal,
    });
    {
        return res.data;
    }
};

export const getStockEventsStatistics = async (id: number) => {
    const res = await axios.get<StockEventsStatistics>(`/API/v2/id/${id}/events/statistics`);

    return res.data;
};

export const getStockEventsStatisticsFlags = async (id: number) => {
    const res = await axios.get<StockEventsStatistics>(`/interface/api/events/company/${id}/statistics`);

    return res.data;
};

// Feeds the "events" flags on the short-interest chart. The legacy
// /API/{id}/events/none/none feed is deprecated and returns no data, so we read
// the same modern company-events list the Events page uses and map each row to
// the chart flag shape ({ x, title, text }).
//
// The list is oldest-first, so an early `from_date` on a high-volume company (e.g.
// Amazon) fills the first page with old events and hides the recent ones. We instead
// pull a recent rolling window — the same 3-year window the Events page uses — which
// keeps this to a single request whose newest events are the ones users care about.
export const getStockEvents = async (companyId: number): Promise<StockEvents[]> => {
    // Mirror the Events page "Price & Events" flags: a short type-initials label
    // on the flag (so long types like "Earnings" don't overflow the circlepin) and
    // a formatted HTML tooltip (bold type header + the full event title beneath).
    const initials = (name: string) =>
        (name ?? "")
            .split(" ")
            .map((w) => w[0]?.toUpperCase() || "")
            .join("");

    // Flag tooltips do not auto-wrap, so break long titles onto multiple
    // lines at word boundaries to keep the tooltip from stretching off-screen.
    const wrap = (text: string, max = 44) => {
        const lines: string[] = [];
        let line = "";
        for (const word of (text ?? "").split(" ")) {
            if (line && (line + " " + word).length > max) {
                lines.push(line);
                line = word;
            } else {
                line = line ? `${line} ${word}` : word;
            }
        }
        if (line) lines.push(line);
        return lines.join("<br/>");
    };

    try {
        const res = await axios.get<List<EventListRow, EventListHeaders>>(`/interface/api/events/company/${companyId}/list`, {
            params: { page_size: 500, page: 1, from_date: dayjs().subtract(3, "year").format("YYYY-MM-DD") },
        });

        return (res.data?.rows ?? []).map((row) => {
            const type = row.type || "Event";
            return {
                x: row.date,
                title: initials(type) || "•",
                text: `<b>${type}</b><br/>${wrap(row.title)}`,
                type,
            };
        });
    } catch (err) {
        // Blobs are supplementary to the price / short-interest series, so a failed
        // events fetch must never reject the chart's Promise.all — fail soft to [].
        if (axios.isCancel(err)) throw err;
        return [];
    }
};

export const getStockEventsResultsStatistics = async (company_id: number) => {
    const res = await axios.get<NewStockEventsStatistics>(`/interface/api/events/company/${company_id}/statistics/quarterly`);

    return res.data;
};
